import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import {
  useApi,
  ApiError,
  type FrameworkAdditionRow,
  type FrameworkOverlayResponse,
  type FrameworkOverrideRow,
} from "../api/client";
import {
  DEFAULT_DOMAIN_IDS,
  DEFAULT_SUBDOMAIN_IDS,
  frameworkLabel,
  useFramework,
  type MergedDomain,
  type MergedSubdomain,
  type PouId,
} from "../framework";
import "./FrameworkEditor.css";

/**
 * Admin editor for the framework structure at /admin/framework.
 *
 * Drill-down UX:
 *   1. Page shows clickable domain tiles in a grid.
 *   2. Click a tile → modal opens for that domain.
 *      - Domain settings panel at the top (rename, hide, delete-if-custom).
 *      - Subdomain list with Edit + Delete buttons per row.
 *      - "Add subdomain" button at the bottom.
 *   3. Inside the modal, clicking "Edit" or "Add subdomain" switches the
 *      modal body to a form view; "Back" returns to the list.
 *
 * All edits queue into local `additions` / `overrides` state. The page-level
 * sticky save bar persists everything atomically via PUT /admin/framework
 * and shows the pending-change count.
 *
 * Permission: admin.framework-content (defaults to administrator+).
 *
 * Conservative defaults: 'Hide' on a default doesn't delete whānau data —
 * historical assessments still render with a 'Retired' chip on the profile.
 * Custom additions can be fully deleted from the overlay; their data
 * lifecycle is the admin's responsibility before deletion.
 */

const POU_OPTIONS: { id: PouId; label: string }[] = [
  { id: "ihorangi", label: "Te Pou Ihorangi" },
  { id: "hineahuone", label: "Te Pou o Hineahuone" },
  { id: "aoturoa", label: "Te Pou Aotūroa" },
  { id: "hinetitama", label: "Te Pou Hinetītama" },
];

const SLUG_PATTERN = /^[a-z][a-z0-9-]{1,79}$/;

type LocalAddition = FrameworkAdditionRow & { localId: string };
type LocalOverride = Omit<FrameworkOverrideRow, "updated_at">;
// Shape of a single patch passed to updateOverride — every field optional so
// callers can target just the slice they're touching (rename, hide, or ToC).
type OverridePatch = Partial<Omit<LocalOverride, "kind" | "id">>;

type LoadState =
  | { kind: "loading" }
  | { kind: "ok" }
  | { kind: "error"; message: string };

type SaveState =
  | { kind: "idle" }
  | { kind: "saving" }
  | { kind: "saved" }
  | { kind: "error"; message: string };

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// ============================================================================
// Top-level page
// ============================================================================

export function FrameworkEditor() {
  const { t } = useTranslation();
  const callApi = useApi();
  const { refresh } = useFramework();

  const [loadState, setLoadState] = useState<LoadState>({ kind: "loading" });
  const [saveState, setSaveState] = useState<SaveState>({ kind: "idle" });
  const [additions, setAdditions] = useState<LocalAddition[]>([]);
  const [overrides, setOverrides] = useState<LocalOverride[]>([]);
  const [originalSnapshot, setOriginalSnapshot] = useState<string>("");

  // Drill-down state.
  const [selectedDomainId, setSelectedDomainId] = useState<string | null>(null);
  const [addingDomain, setAddingDomain] = useState(false);

  // Load existing overlay on mount.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data =
          await callApi<FrameworkOverlayResponse>("/admin/framework");
        if (cancelled) return;
        const adds: LocalAddition[] = data.additions.map((a, i) => ({
          ...a,
          localId: `existing-${i}-${a.kind}-${a.id}`,
        }));
        const overs: LocalOverride[] = data.overrides.map((o) => ({
          kind: o.kind,
          id: o.id,
          name_en: o.name_en,
          name_mi: o.name_mi,
          toc_outcome: o.toc_outcome,
          hidden: o.hidden,
        }));
        setAdditions(adds);
        setOverrides(overs);
        setOriginalSnapshot(
          JSON.stringify({ additions: adds, overrides: overs }),
        );
        setLoadState({ kind: "ok" });
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof Error
            ? err.message
            : "Failed to load framework overlay";
        setLoadState({ kind: "error", message });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentSnapshot = useMemo(
    () => JSON.stringify({ additions, overrides }),
    [additions, overrides],
  );
  const isDirty =
    currentSnapshot !== originalSnapshot && originalSnapshot !== "";

  // ---- Helpers passed down ------------------------------------------------

  const overrideFor = (
    kind: "domain" | "subdomain",
    id: string,
  ): LocalOverride | undefined =>
    overrides.find((o) => o.kind === kind && o.id === id);

  function updateOverride(
    kind: "domain" | "subdomain",
    id: string,
    patch: OverridePatch,
  ) {
    setOverrides((prev) => {
      const existing = prev.find((o) => o.kind === kind && o.id === id);
      const merged: LocalOverride = {
        kind,
        id,
        name_en: existing?.name_en ?? null,
        name_mi: existing?.name_mi ?? null,
        toc_outcome: existing?.toc_outcome ?? null,
        hidden: existing?.hidden ?? false,
        ...patch,
      };
      const isNoOp =
        merged.name_en === null &&
        merged.name_mi === null &&
        merged.toc_outcome === null &&
        merged.hidden === false;
      if (isNoOp) return prev.filter((o) => !(o.kind === kind && o.id === id));
      const idx = prev.findIndex((o) => o.kind === kind && o.id === id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = merged;
        return next;
      }
      return [...prev, merged];
    });
    setSaveState({ kind: "idle" });
  }

  function addAddition(
    addition: Omit<LocalAddition, "localId" | "updated_at">,
  ) {
    setAdditions((prev) => [
      ...prev,
      {
        ...addition,
        localId: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        updated_at: new Date().toISOString(),
      },
    ]);
    setSaveState({ kind: "idle" });
  }

  function removeAddition(localId: string) {
    setAdditions((prev) => prev.filter((a) => a.localId !== localId));
    setSaveState({ kind: "idle" });
  }

  function updateAddition(localId: string, patch: Partial<LocalAddition>) {
    setAdditions((prev) =>
      prev.map((a) => (a.localId === localId ? { ...a, ...patch } : a)),
    );
    setSaveState({ kind: "idle" });
  }

  // ---- Save / Reset -------------------------------------------------------

  async function handleSave() {
    setSaveState({ kind: "saving" });
    try {
      const body = {
        additions: additions.map(
          ({ localId: _l, updated_at: _u, ...rest }) => rest,
        ),
        overrides: overrides.map(
          ({ kind, id, name_en, name_mi, toc_outcome, hidden }) => ({
            kind,
            id,
            name_en,
            name_mi,
            toc_outcome,
            hidden,
          }),
        ),
      };
      await callApi("/admin/framework", {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      });
      setOriginalSnapshot(currentSnapshot);
      setSaveState({ kind: "saved" });
      await refresh();
    } catch (err) {
      const message =
        err instanceof ApiError
          ? typeof (err.body as { error?: unknown })?.error === "string"
            ? (err.body as { error: string }).error
            : err.message
          : err instanceof Error
            ? err.message
            : "Failed to save framework overlay";
      setSaveState({ kind: "error", message });
    }
  }

  function handleReset() {
    if (!isDirty) return;
    const parsed = JSON.parse(originalSnapshot) as {
      additions: LocalAddition[];
      overrides: LocalOverride[];
    };
    setAdditions(parsed.additions);
    setOverrides(parsed.overrides);
    setSaveState({ kind: "idle" });
  }

  const pendingCount = useMemo(
    () => computePendingCount(additions, overrides, originalSnapshot, isDirty),
    [additions, overrides, originalSnapshot, isDirty],
  );

  // ---- Domain grid data ---------------------------------------------------

  const { domains: persistedDomains } = useFramework();
  const tiles = useMemo(() => {
    // For the grid: synthesize a list of "tile" entries. Each is either a default
    // domain (with merged live edit state applied) or a custom addition.
    const out: Array<{
      id: string;
      label: string;
      source: "default" | "custom";
      hidden: boolean;
      subdomainCount: number;
    }> = [];

    for (const id of DEFAULT_DOMAIN_IDS) {
      const persisted = persistedDomains.find((d) => d.id === id);
      const override = overrideFor("domain", id);
      const headingItem: MergedDomain = {
        id,
        nameEn: override?.name_en ?? persisted?.nameEn ?? id,
        nameMi: override?.name_mi ?? null,
        source: "default",
        hasNameOverride: !!override,
      };
      const subCount =
        // Defaults: 18 distributed across 6 — count by walking DEFAULT_SUBDOMAIN_IDS
        defaultSubdomainCountForDomain(id) +
        additions.filter((a) => a.kind === "subdomain" && a.domain_id === id)
          .length;
      out.push({
        id,
        label: frameworkLabel(
          headingItem,
          "domain",
          t("app.lang") === "mi" ? "mi" : "en",
          t,
        ),
        source: "default",
        hidden: override?.hidden === true,
        subdomainCount: subCount,
      });
    }
    for (const a of additions.filter((a) => a.kind === "domain")) {
      const subCount = additions.filter(
        (s) => s.kind === "subdomain" && s.domain_id === a.id,
      ).length;
      out.push({
        id: a.id,
        label: a.name_en,
        source: "custom",
        hidden: false,
        subdomainCount: subCount,
      });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [additions, overrides, persistedDomains, t]);

  if (loadState.kind === "loading") {
    return (
      <section className="framework-editor">
        <h1 className="framework-editor__title">
          {t("frameworkEditor.title")}
        </h1>
        <p className="framework-editor__status">
          {t("frameworkEditor.loading")}
        </p>
      </section>
    );
  }
  if (loadState.kind === "error") {
    return (
      <section className="framework-editor">
        <h1 className="framework-editor__title">
          {t("frameworkEditor.title")}
        </h1>
        <p className="framework-editor__error">{loadState.message}</p>
      </section>
    );
  }

  const selectedDomain = selectedDomainId
    ? tiles.find((tile) => tile.id === selectedDomainId)
    : null;

  return (
    <section className="framework-editor">
      <header className="framework-editor__header">
        <h1 className="framework-editor__title">
          {t("frameworkEditor.title")}
        </h1>
        <p className="framework-editor__subtitle">
          {t("frameworkEditor.subtitle")}
        </p>
      </header>

      <div className="fe-grid">
        {tiles.map((tile) => (
          <button
            key={tile.id}
            type="button"
            className={`fe-tile ${tile.hidden ? "fe-tile--hidden" : ""} fe-tile--${tile.source}`}
            onClick={() => setSelectedDomainId(tile.id)}
          >
            <span className="fe-tile__heading">{tile.label}</span>
            <span className="fe-tile__meta">
              <span className={`fe-chip fe-chip--${tile.source}`}>
                {tile.source === "default"
                  ? t("frameworkEditor.chipDefault")
                  : t("frameworkEditor.chipCustom")}
              </span>
              {tile.hidden && (
                <span className="fe-chip fe-chip--warn">
                  {t("frameworkEditor.chipHidden")}
                </span>
              )}
            </span>
            <span className="fe-tile__count">
              {t("frameworkEditor.subdomainCount", {
                count: tile.subdomainCount,
              })}
            </span>
          </button>
        ))}

        <button
          type="button"
          className="fe-tile fe-tile--add"
          onClick={() => setAddingDomain(true)}
        >
          <span className="fe-tile__heading">
            + {t("frameworkEditor.addDomain")}
          </span>
        </button>
      </div>

      {/* Domain detail modal */}
      {selectedDomain && (
        <DomainModal
          domainId={selectedDomain.id}
          source={selectedDomain.source}
          additions={additions}
          overrideFor={overrideFor}
          onOverrideChange={updateOverride}
          onAddAddition={addAddition}
          onRemoveAddition={removeAddition}
          onUpdateAddition={updateAddition}
          onClose={() => setSelectedDomainId(null)}
        />
      )}

      {/* Add domain modal */}
      {addingDomain && (
        <AddDomainModal
          existingIds={
            new Set<string>([
              ...DEFAULT_DOMAIN_IDS,
              ...additions.filter((a) => a.kind === "domain").map((a) => a.id),
            ])
          }
          onAdd={(a) => {
            addAddition(a);
            setAddingDomain(false);
          }}
          onClose={() => setAddingDomain(false)}
        />
      )}

      {/* Sticky save bar */}
      <div
        className={`framework-editor__savebar ${isDirty ? "framework-editor__savebar--dirty" : ""}`}
      >
        <div className="framework-editor__savebar-status">
          {saveState.kind === "saving" && t("frameworkEditor.saving")}
          {saveState.kind === "saved" && !isDirty && t("frameworkEditor.saved")}
          {saveState.kind === "error" && (
            <span className="framework-editor__error">{saveState.message}</span>
          )}
          {isDirty && saveState.kind !== "saving" && (
            <span className="framework-editor__pending">
              {t("frameworkEditor.pending", { count: pendingCount })}
            </span>
          )}
        </div>
        <div className="framework-editor__savebar-actions">
          <button
            type="button"
            className="framework-editor__button"
            disabled={!isDirty || saveState.kind === "saving"}
            onClick={handleReset}
          >
            {t("frameworkEditor.discard")}
          </button>
          <button
            type="button"
            className="framework-editor__button framework-editor__button--primary"
            disabled={!isDirty || saveState.kind === "saving"}
            onClick={() => void handleSave()}
          >
            {t("frameworkEditor.save")}
          </button>
        </div>
      </div>
    </section>
  );
}

export default FrameworkEditor;

// ============================================================================
// Helper: count code-default subdomains for a domain id
// ============================================================================

let _defaultSubdomainCountsCache: Map<string, number> | null = null;
function defaultSubdomainCountForDomain(domainId: string): number {
  if (_defaultSubdomainCountsCache === null) {
    // Lazy-build the count map from the framework module. We can't import
    // DEFAULT_SUBDOMAINS directly here because we want the typed defaults
    // — use the find* helper via a static walk.
    _defaultSubdomainCountsCache = new Map<string, number>();
    // We don't have direct access to DEFAULT_SUBDOMAINS in this file; the
    // hook returns the persisted-merged view which includes hidden items if
    // they're hidden... so we count using the runtime helper instead via
    // a module-level lazy import at call time. Simpler: walk
    // DEFAULT_SUBDOMAIN_IDS via the imported set and look up domain.
  }
  // Counting from the imported DEFAULT_SUBDOMAIN_IDS Set requires us to also
  // know each id's domain. Easiest path: query the framework context. We
  // do this through the consumer (DomainModal) instead — for the tile
  // count we re-derive on every render.
  // Implemented inline above via DEFAULT_SUBDOMAINS import? — we don't import
  // it. So return 0 here and let the tile fall back to the hook-derived count.
  return defaultSubdomainCountByDomain[domainId] ?? 0;
}

// Hardcoded per-domain default counts. Mirrors the source of truth in
// src/framework/data.ts but is denormalised here to avoid importing the full
// array. If you change the framework defaults, update both places.
const defaultSubdomainCountByDomain: Record<string, number> = {
  "wider-connections": 3,
  "personal-connections": 4,
  education: 2,
  health: 5,
  "financial-stability": 3,
  housing: 1,
};

// ============================================================================
// Domain modal — list of subdomains + edit/add sub-views
// ============================================================================

type ModalView =
  | { kind: "list" }
  | {
      kind: "edit-subdomain";
      subdomainId: string;
      source: "default" | "custom";
      localId?: string;
    }
  | { kind: "add-subdomain" }
  | { kind: "edit-domain" };

function DomainModal({
  domainId,
  source,
  additions,
  overrideFor,
  onOverrideChange,
  onAddAddition,
  onRemoveAddition,
  onUpdateAddition,
  onClose,
}: {
  domainId: string;
  source: "default" | "custom";
  additions: LocalAddition[];
  overrideFor: (
    kind: "domain" | "subdomain",
    id: string,
  ) => LocalOverride | undefined;
  onOverrideChange: (
    kind: "domain" | "subdomain",
    id: string,
    patch: Partial<Omit<LocalOverride, "kind" | "id">>,
  ) => void;
  onAddAddition: (a: Omit<LocalAddition, "localId" | "updated_at">) => void;
  onRemoveAddition: (localId: string) => void;
  onUpdateAddition: (localId: string, patch: Partial<LocalAddition>) => void;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { findSubdomain, findDomain } = useFramework();
  const [view, setView] = useState<ModalView>({ kind: "list" });

  const domainAddition =
    source === "custom"
      ? additions.find((a) => a.kind === "domain" && a.id === domainId)
      : undefined;
  const domainOverride = overrideFor("domain", domainId);

  // Synthesize a MergedDomain for the header label.
  const persistedDomain = findDomain(domainId);
  const headingDomain: MergedDomain = {
    id: domainId,
    nameEn:
      domainAddition?.name_en ??
      domainOverride?.name_en ??
      persistedDomain?.nameEn ??
      domainId,
    nameMi: domainAddition?.name_mi ?? domainOverride?.name_mi ?? null,
    source,
    hasNameOverride: source === "custom" || domainOverride != null,
  };
  const heading = frameworkLabel(headingDomain, "domain", i18n.language, t);

  // Collect subdomain rows for the list view.
  const subdomainRows: Array<
    | { source: "default"; subdomain: MergedSubdomain }
    | { source: "custom"; subdomain: MergedSubdomain; addition: LocalAddition }
  > = [];

  for (const subId of DEFAULT_SUBDOMAIN_IDS) {
    const sub = findSubdomain(subId);
    if (sub && sub.source === "default" && sub.domainId === domainId) {
      // Apply live override to display label
      const o = overrideFor("subdomain", subId);
      subdomainRows.push({
        source: "default",
        subdomain: {
          ...sub,
          nameEn: o?.name_en ?? sub.nameEn,
          nameMi: o?.name_mi ?? sub.nameMi,
          hasNameOverride: !!(o && (o.name_en != null || o.name_mi != null)),
        },
      });
    }
  }
  for (const a of additions.filter(
    (a) => a.kind === "subdomain" && a.domain_id === domainId,
  )) {
    subdomainRows.push({
      source: "custom",
      subdomain: {
        id: a.id,
        domainId: a.domain_id ?? domainId,
        pouId: (a.pou_id as PouId | null) ?? "aoturoa",
        nameEn: a.name_en,
        nameMi: a.name_mi,
        toCOutcome: a.toc_outcome ?? "",
        source: "custom",
        hasNameOverride: true,
      },
      addition: a,
    });
  }

  function renderBody() {
    if (view.kind === "list") {
      return (
        <>
          <DomainSettingsPanel
            domainId={domainId}
            source={source}
            domainAddition={domainAddition}
            domainOverride={domainOverride}
            onOverrideChange={onOverrideChange}
            onUpdateAddition={onUpdateAddition}
            onRemoveAddition={() => {
              if (
                domainAddition &&
                window.confirm(t("frameworkEditor.confirmDeleteDomain"))
              ) {
                onRemoveAddition(domainAddition.localId);
                onClose();
              }
            }}
          />

          <section className="fe-modal__section">
            <header className="fe-modal__section-header">
              <h3 className="fe-modal__section-title">
                {t("frameworkEditor.subdomainsHeading")}{" "}
                <span className="fe-modal__count">
                  ({subdomainRows.length})
                </span>
              </h3>
              <button
                type="button"
                className="fe-button fe-button--ghost"
                onClick={() => setView({ kind: "add-subdomain" })}
              >
                + {t("frameworkEditor.addSubdomain")}
              </button>
            </header>

            <ul className="fe-modal__sub-list">
              {subdomainRows.map((row) => {
                const label = frameworkLabel(
                  row.subdomain,
                  "subdomain",
                  i18n.language,
                  t,
                );
                const hidden =
                  overrideFor("subdomain", row.subdomain.id)?.hidden === true;
                return (
                  <li
                    key={row.subdomain.id}
                    className={`fe-modal__sub-row ${hidden ? "fe-modal__sub-row--hidden" : ""}`}
                  >
                    <div className="fe-modal__sub-row-main">
                      <span className="fe-modal__sub-name">{label}</span>
                      <span
                        className={`fe-chip fe-chip--small fe-chip--${row.source}`}
                      >
                        {row.source === "default"
                          ? t("frameworkEditor.chipDefault")
                          : t("frameworkEditor.chipCustom")}
                      </span>
                      {hidden && (
                        <span className="fe-chip fe-chip--small fe-chip--warn">
                          {t("frameworkEditor.chipHidden")}
                        </span>
                      )}
                    </div>
                    <div className="fe-modal__sub-row-actions">
                      <button
                        type="button"
                        className="fe-button"
                        onClick={() =>
                          setView({
                            kind: "edit-subdomain",
                            subdomainId: row.subdomain.id,
                            source: row.source,
                            localId:
                              row.source === "custom"
                                ? row.addition.localId
                                : undefined,
                          })
                        }
                      >
                        {t("frameworkEditor.edit")}
                      </button>
                      {row.source === "custom" && (
                        <button
                          type="button"
                          className="fe-button fe-button--danger"
                          onClick={() => {
                            if (
                              window.confirm(
                                t("frameworkEditor.confirmDeleteSubdomain"),
                              )
                            ) {
                              onRemoveAddition(row.addition.localId);
                            }
                          }}
                        >
                          {t("frameworkEditor.delete")}
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
              {subdomainRows.length === 0 && (
                <li className="fe-modal__sub-row fe-modal__sub-row--empty">
                  {t("frameworkEditor.noSubdomains")}
                </li>
              )}
            </ul>
          </section>
        </>
      );
    }

    if (view.kind === "edit-subdomain") {
      const targetAddition = view.localId
        ? additions.find((a) => a.localId === view.localId)
        : undefined;
      const sub = findSubdomain(view.subdomainId);
      if (view.source === "custom" && !targetAddition) {
        // Shouldn't happen — fall back to list.
        setView({ kind: "list" });
        return null;
      }
      return (
        <SubdomainEditPanel
          source={view.source}
          subdomain={sub ?? null}
          addition={targetAddition}
          override={overrideFor("subdomain", view.subdomainId)}
          onOverrideChange={(patch) =>
            onOverrideChange("subdomain", view.subdomainId, patch)
          }
          onUpdateAddition={(patch) => {
            if (targetAddition) onUpdateAddition(targetAddition.localId, patch);
          }}
          onBack={() => setView({ kind: "list" })}
        />
      );
    }

    if (view.kind === "add-subdomain") {
      const existingIds = new Set<string>([
        ...DEFAULT_SUBDOMAIN_IDS,
        ...additions.filter((a) => a.kind === "subdomain").map((a) => a.id),
      ]);
      return (
        <SubdomainAddPanel
          domainId={domainId}
          existingIds={existingIds}
          onAdd={(a) => {
            onAddAddition(a);
            setView({ kind: "list" });
          }}
          onCancel={() => setView({ kind: "list" })}
        />
      );
    }

    return null;
  }

  // Trap ESC to close (only if on list view; on sub-views ESC goes back).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (view.kind === "list") onClose();
        else setView({ kind: "list" });
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [view.kind, onClose]);

  return createPortal(
    <div
      className="fe-modal__backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget && view.kind === "list") onClose();
      }}
    >
      <div
        className="fe-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="fe-modal-title"
      >
        <header className="fe-modal__header">
          {view.kind !== "list" && (
            <button
              type="button"
              className="fe-modal__back"
              onClick={() => setView({ kind: "list" })}
              aria-label={t("frameworkEditor.back")}
            >
              ←
            </button>
          )}
          <h2 id="fe-modal-title" className="fe-modal__title">
            {view.kind === "edit-subdomain"
              ? t("frameworkEditor.editSubdomainTitle")
              : view.kind === "add-subdomain"
                ? t("frameworkEditor.addSubdomainTitle")
                : heading}
          </h2>
          <button
            type="button"
            className="fe-modal__close"
            onClick={onClose}
            aria-label={t("frameworkEditor.close")}
          >
            ×
          </button>
        </header>
        <div className="fe-modal__body">{renderBody()}</div>
        {view.kind === "list" && (
          <footer className="fe-modal__footer">
            <button
              type="button"
              className="fe-button fe-button--primary"
              onClick={onClose}
            >
              {t("frameworkEditor.done")}
            </button>
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}

// ============================================================================
// Domain settings panel (rename + hide + delete-custom)
// ============================================================================

function DomainSettingsPanel({
  domainId,
  source,
  domainAddition,
  domainOverride,
  onOverrideChange,
  onUpdateAddition,
  onRemoveAddition,
}: {
  domainId: string;
  source: "default" | "custom";
  domainAddition?: LocalAddition;
  domainOverride?: LocalOverride;
  onOverrideChange: (
    kind: "domain" | "subdomain",
    id: string,
    patch: Partial<Omit<LocalOverride, "kind" | "id">>,
  ) => void;
  onUpdateAddition: (localId: string, patch: Partial<LocalAddition>) => void;
  onRemoveAddition: () => void;
}) {
  const { t } = useTranslation();
  const { findDomain } = useFramework();
  const defaultDomain = findDomain(domainId);
  const fallbackNameEn = defaultDomain?.nameEn ?? domainId;

  if (source === "custom" && domainAddition) {
    return (
      <section className="fe-modal__section">
        <h3 className="fe-modal__section-title">
          {t("frameworkEditor.domainSettings")}
        </h3>
        <div className="fe-fields">
          <label className="fe-field">
            <span className="fe-field__label">
              {t("frameworkEditor.nameEn")}
            </span>
            <input
              type="text"
              value={domainAddition.name_en}
              onChange={(e) =>
                onUpdateAddition(domainAddition.localId, {
                  name_en: e.target.value,
                })
              }
            />
          </label>
          <label className="fe-field">
            <span className="fe-field__label">
              {t("frameworkEditor.nameMi")}
            </span>
            <input
              type="text"
              lang="mi"
              value={domainAddition.name_mi ?? ""}
              onChange={(e) =>
                onUpdateAddition(domainAddition.localId, {
                  name_mi: e.target.value.trim() === "" ? null : e.target.value,
                })
              }
            />
          </label>
        </div>
        <button
          type="button"
          className="fe-button fe-button--danger"
          onClick={onRemoveAddition}
        >
          {t("frameworkEditor.removeCustomDomain")}
        </button>
      </section>
    );
  }

  // Default domain — rename + hide only
  return (
    <section className="fe-modal__section">
      <h3 className="fe-modal__section-title">
        {t("frameworkEditor.domainSettings")}
      </h3>
      <div className="fe-fields">
        <label className="fe-field">
          <span className="fe-field__label">
            {t("frameworkEditor.renameEn")}
          </span>
          <input
            type="text"
            value={domainOverride?.name_en ?? ""}
            placeholder={fallbackNameEn}
            onChange={(e) =>
              onOverrideChange("domain", domainId, {
                name_en: e.target.value.trim() === "" ? null : e.target.value,
              })
            }
          />
        </label>
        <label className="fe-field">
          <span className="fe-field__label">
            {t("frameworkEditor.renameMi")}
          </span>
          <input
            type="text"
            lang="mi"
            value={domainOverride?.name_mi ?? ""}
            placeholder={t("frameworkEditor.renameMiPlaceholder")}
            onChange={(e) =>
              onOverrideChange("domain", domainId, {
                name_mi: e.target.value.trim() === "" ? null : e.target.value,
              })
            }
          />
        </label>
      </div>
      <label className="fe-toggle">
        <input
          type="checkbox"
          checked={domainOverride?.hidden ?? false}
          onChange={(e) =>
            onOverrideChange("domain", domainId, { hidden: e.target.checked })
          }
        />
        <span>{t("frameworkEditor.hideDomain")}</span>
      </label>
    </section>
  );
}

// ============================================================================
// Subdomain edit panel (in-modal sub-view)
// ============================================================================

function SubdomainEditPanel({
  source,
  subdomain,
  addition,
  override,
  onOverrideChange,
  onUpdateAddition,
  onBack,
}: {
  source: "default" | "custom";
  subdomain: MergedSubdomain | null;
  addition?: LocalAddition;
  override?: LocalOverride;
  onOverrideChange: (
    patch: Partial<Omit<LocalOverride, "kind" | "id">>,
  ) => void;
  onUpdateAddition: (patch: Partial<LocalAddition>) => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();

  if (source === "custom" && addition) {
    return (
      <section className="fe-modal__section">
        <div className="fe-fields">
          <label className="fe-field">
            <span className="fe-field__label">
              {t("frameworkEditor.nameEn")}
            </span>
            <input
              type="text"
              value={addition.name_en}
              onChange={(e) => onUpdateAddition({ name_en: e.target.value })}
              autoFocus
            />
          </label>
          <label className="fe-field">
            <span className="fe-field__label">
              {t("frameworkEditor.nameMi")}
            </span>
            <input
              type="text"
              lang="mi"
              value={addition.name_mi ?? ""}
              onChange={(e) =>
                onUpdateAddition({
                  name_mi: e.target.value.trim() === "" ? null : e.target.value,
                })
              }
            />
          </label>
          <label className="fe-field">
            <span className="fe-field__label">{t("frameworkEditor.pou")}</span>
            <select
              value={addition.pou_id ?? "aoturoa"}
              onChange={(e) => onUpdateAddition({ pou_id: e.target.value })}
            >
              {POU_OPTIONS.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
          <label className="fe-field fe-field--wide">
            <span className="fe-field__label">
              {t("frameworkEditor.tocOutcome")}
            </span>
            <textarea
              rows={2}
              value={addition.toc_outcome ?? ""}
              onChange={(e) =>
                onUpdateAddition({
                  toc_outcome:
                    e.target.value.trim() === "" ? null : e.target.value,
                })
              }
            />
          </label>
        </div>
        <div className="fe-actions">
          <button
            type="button"
            className="fe-button fe-button--primary"
            onClick={onBack}
          >
            {t("frameworkEditor.done")}
          </button>
        </div>
      </section>
    );
  }

  // Default subdomain — rename + hide only
  if (!subdomain) {
    return (
      <section className="fe-modal__section">
        <p className="fe-error">{t("frameworkEditor.subdomainNotFound")}</p>
      </section>
    );
  }
  return (
    <section className="fe-modal__section">
      <p className="fe-modal__hint">
        {t("frameworkEditor.editDefaultHint", { fallback: subdomain.nameEn })}
      </p>
      <div className="fe-fields">
        <label className="fe-field">
          <span className="fe-field__label">
            {t("frameworkEditor.renameEn")}
          </span>
          <input
            type="text"
            value={override?.name_en ?? ""}
            placeholder={subdomain.nameEn}
            onChange={(e) =>
              onOverrideChange({
                name_en: e.target.value.trim() === "" ? null : e.target.value,
              })
            }
            autoFocus
          />
        </label>
        <label className="fe-field">
          <span className="fe-field__label">
            {t("frameworkEditor.renameMi")}
          </span>
          <input
            type="text"
            lang="mi"
            value={override?.name_mi ?? ""}
            placeholder={t("frameworkEditor.renameMiPlaceholder")}
            onChange={(e) =>
              onOverrideChange({
                name_mi: e.target.value.trim() === "" ? null : e.target.value,
              })
            }
          />
        </label>
        <label className="fe-field fe-field--wide">
          <span className="fe-field__label">
            {t("frameworkEditor.tocOutcome")}
          </span>
          <textarea
            rows={2}
            value={override?.toc_outcome ?? ""}
            placeholder={subdomain.toCOutcome}
            onChange={(e) =>
              onOverrideChange({
                toc_outcome:
                  e.target.value.trim() === "" ? null : e.target.value,
              })
            }
          />
        </label>
      </div>
      <label className="fe-toggle">
        <input
          type="checkbox"
          checked={override?.hidden ?? false}
          onChange={(e) => onOverrideChange({ hidden: e.target.checked })}
        />
        <span>{t("frameworkEditor.hideSubdomain")}</span>
      </label>
      <div className="fe-actions">
        <button
          type="button"
          className="fe-button fe-button--primary"
          onClick={onBack}
        >
          {t("frameworkEditor.done")}
        </button>
      </div>
    </section>
  );
}

// ============================================================================
// Subdomain add panel (in-modal sub-view)
// ============================================================================

function SubdomainAddPanel({
  domainId,
  existingIds,
  onAdd,
  onCancel,
}: {
  domainId: string;
  existingIds: ReadonlySet<string>;
  onAdd: (a: Omit<LocalAddition, "localId" | "updated_at">) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [nameEn, setNameEn] = useState("");
  const [nameMi, setNameMi] = useState("");
  const [pouId, setPouId] = useState<PouId>("aoturoa");
  const [tocOutcome, setTocOutcome] = useState("");
  const [error, setError] = useState<string | null>(null);

  function handleAdd() {
    const trimmedEn = nameEn.trim();
    if (trimmedEn === "") {
      setError(t("frameworkEditor.errorNameEnRequired"));
      return;
    }
    const id = slugify(trimmedEn);
    if (!SLUG_PATTERN.test(id)) {
      setError(t("frameworkEditor.errorSlugInvalid"));
      return;
    }
    if (existingIds.has(id)) {
      setError(t("frameworkEditor.errorIdInUse", { id }));
      return;
    }
    onAdd({
      kind: "subdomain",
      id,
      name_en: trimmedEn,
      name_mi: nameMi.trim() === "" ? null : nameMi.trim(),
      domain_id: domainId,
      pou_id: pouId,
      toc_outcome: tocOutcome.trim() === "" ? null : tocOutcome.trim(),
      sort_order: 100,
    });
  }

  return (
    <section className="fe-modal__section">
      <div className="fe-fields">
        <label className="fe-field">
          <span className="fe-field__label">{t("frameworkEditor.nameEn")}</span>
          <input
            type="text"
            value={nameEn}
            onChange={(e) => setNameEn(e.target.value)}
            autoFocus
          />
        </label>
        <label className="fe-field">
          <span className="fe-field__label">{t("frameworkEditor.nameMi")}</span>
          <input
            type="text"
            lang="mi"
            value={nameMi}
            onChange={(e) => setNameMi(e.target.value)}
          />
        </label>
        <label className="fe-field">
          <span className="fe-field__label">{t("frameworkEditor.pou")}</span>
          <select
            value={pouId}
            onChange={(e) => setPouId(e.target.value as PouId)}
          >
            {POU_OPTIONS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </label>
        <label className="fe-field fe-field--wide">
          <span className="fe-field__label">
            {t("frameworkEditor.tocOutcome")}
          </span>
          <textarea
            rows={2}
            value={tocOutcome}
            onChange={(e) => setTocOutcome(e.target.value)}
          />
        </label>
      </div>
      {error && <p className="fe-error">{error}</p>}
      <div className="fe-actions">
        <button type="button" className="fe-button" onClick={onCancel}>
          {t("frameworkEditor.cancel")}
        </button>
        <button
          type="button"
          className="fe-button fe-button--primary"
          onClick={handleAdd}
        >
          {t("frameworkEditor.addSubdomain")}
        </button>
      </div>
    </section>
  );
}

// ============================================================================
// Add domain modal (top-level)
// ============================================================================

function AddDomainModal({
  existingIds,
  onAdd,
  onClose,
}: {
  existingIds: ReadonlySet<string>;
  onAdd: (a: Omit<LocalAddition, "localId" | "updated_at">) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [nameEn, setNameEn] = useState("");
  const [nameMi, setNameMi] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  function handleAdd() {
    const trimmedEn = nameEn.trim();
    if (trimmedEn === "") {
      setError(t("frameworkEditor.errorNameEnRequired"));
      return;
    }
    const id = slugify(trimmedEn);
    if (!SLUG_PATTERN.test(id)) {
      setError(t("frameworkEditor.errorSlugInvalid"));
      return;
    }
    if (existingIds.has(id)) {
      setError(t("frameworkEditor.errorIdInUse", { id }));
      return;
    }
    onAdd({
      kind: "domain",
      id,
      name_en: trimmedEn,
      name_mi: nameMi.trim() === "" ? null : nameMi.trim(),
      domain_id: null,
      pou_id: null,
      toc_outcome: null,
      sort_order: 100,
    });
  }

  return createPortal(
    <div
      className="fe-modal__backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="fe-modal fe-modal--small" role="dialog" aria-modal="true">
        <header className="fe-modal__header">
          <h2 className="fe-modal__title">{t("frameworkEditor.addDomain")}</h2>
          <button
            type="button"
            className="fe-modal__close"
            onClick={onClose}
            aria-label={t("frameworkEditor.close")}
          >
            ×
          </button>
        </header>
        <div className="fe-modal__body">
          <section className="fe-modal__section">
            <div className="fe-fields">
              <label className="fe-field">
                <span className="fe-field__label">
                  {t("frameworkEditor.nameEn")}
                </span>
                <input
                  type="text"
                  value={nameEn}
                  onChange={(e) => setNameEn(e.target.value)}
                  autoFocus
                />
              </label>
              <label className="fe-field">
                <span className="fe-field__label">
                  {t("frameworkEditor.nameMi")}
                </span>
                <input
                  type="text"
                  lang="mi"
                  value={nameMi}
                  onChange={(e) => setNameMi(e.target.value)}
                />
              </label>
            </div>
            {error && <p className="fe-error">{error}</p>}
          </section>
        </div>
        <footer className="fe-modal__footer">
          <button type="button" className="fe-button" onClick={onClose}>
            {t("frameworkEditor.cancel")}
          </button>
          <button
            type="button"
            className="fe-button fe-button--primary"
            onClick={handleAdd}
          >
            {t("frameworkEditor.addDomain")}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

// ============================================================================
// Pending count helper
// ============================================================================

function computePendingCount(
  additions: LocalAddition[],
  overrides: LocalOverride[],
  originalSnapshot: string,
  isDirty: boolean,
): number {
  if (!isDirty) return 0;
  try {
    const orig = JSON.parse(originalSnapshot) as {
      additions: LocalAddition[];
      overrides: LocalOverride[];
    };
    const origAdds = new Set(orig.additions.map((a) => `${a.kind}:${a.id}`));
    const origOvers = new Map(
      orig.overrides.map((o) => [`${o.kind}:${o.id}`, o]),
    );
    const currentAdds = new Set(additions.map((a) => `${a.kind}:${a.id}`));
    const currentOvers = new Map(
      overrides.map((o) => [`${o.kind}:${o.id}`, o]),
    );
    const origAddMap = new Map(
      orig.additions.map((a) => [`${a.kind}:${a.id}`, a]),
    );

    let n = 0;
    for (const a of additions) if (!origAdds.has(`${a.kind}:${a.id}`)) n++;
    for (const k of origAdds) if (!currentAdds.has(k)) n++;
    for (const [k, o] of currentOvers) {
      const before = origOvers.get(k);
      if (
        !before ||
        before.name_en !== o.name_en ||
        before.name_mi !== o.name_mi ||
        before.toc_outcome !== o.toc_outcome ||
        before.hidden !== o.hidden
      ) {
        n++;
      }
    }
    for (const k of origOvers.keys()) if (!currentOvers.has(k)) n++;
    for (const a of additions) {
      const before = origAddMap.get(`${a.kind}:${a.id}`);
      if (
        before &&
        (before.name_en !== a.name_en ||
          before.name_mi !== a.name_mi ||
          before.domain_id !== a.domain_id ||
          before.pou_id !== a.pou_id ||
          before.toc_outcome !== a.toc_outcome ||
          before.sort_order !== a.sort_order)
      ) {
        n++;
      }
    }
    return n;
  } catch {
    return 1;
  }
}
